<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { router, usePage } from "@inertiajs/svelte";
  import { ArrowLeft, Loader2, Save } from "lucide-svelte";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import * as Select from "$lib/components/ui/select/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import { appStore } from '$lib/stores/appStore';

  let { category = {}, parent_options = [] } = $props();

 let name = $state("");
  let slug = $state("");
  let parent_id = $state("");
  let color = $state("#3b82f6");
  let isSubmitting = $state(false);


    $effect.pre(() => {
      name = category.name || "";
      slug = category.slug || "";
      parent_id = category.parent_id || "";
      color = category.color || "#3b82f6";
    });

  const page = usePage();
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.categories || {});

  const isCreatePage = $derived(typeof window !== 'undefined' && window.location.pathname.endsWith('/new'));

  // 💡 将首层“作为独立顶级分类”也纳入多语言配置
  const selectOptions = $derived([
    { value: "", label: t.parent_root_option || "作为独立顶级分类" },
    ...parent_options.map((opt: any) => ({ value: String(opt.value), label: opt.label }))
  ]);

  let currentSelectTriggerText = $derived(
    selectOptions.find(opt => opt.value === String(parent_id))?.label || t.parent_select_hint || "请选择上级分类"
  );

  function handleSubmit(e: Event) {
    e.preventDefault();
    if (isSubmitting) return;
    isSubmitting = true;

    const payload = { 
      category: { 
        name, 
        slug, 
        parent_id: parent_id === "" ? null : parent_id,
        color 
      } 
    };
    
    const categoryId = category?.id;

    if (categoryId && !isCreatePage) {
      router.put(`/categories/${categoryId}`, payload, {
        onFinish: () => isSubmitting = false
      });
    } else {
      router.post("/categories", payload, {
        onFinish: () => isSubmitting = false
      });
    }
  }

  

  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });
</script>

<div class="p-8 max-w-2xl mx-auto">
  <Button 
    variant="ghost" 
    size="sm" 
    onclick={() => router.get('/categories')} 
    class="text-xs text-muted-foreground hover:text-foreground mb-6 -ml-3 gap-1.5"
  >
    <ArrowLeft class="size-3.5" /> {t.back_to_list}
  </Button>

  <div class="mb-6">
    <h1 class="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
      {category?.id && !isCreatePage ? t.edit_title : t.new_title}
    </h1>
    <p class="text-sm text-muted-foreground mt-1">
      {t.editor_subtitle}
    </p>
  </div>

  <form onsubmit={handleSubmit} class="space-y-6 bg-card border dark:border-zinc-800 p-6 rounded-xl shadow-sm bg-white dark:bg-zinc-900">
    <div class="grid grid-cols-2 gap-4">
      <div class="space-y-2">
        <Label for="name" class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.field_name}</Label>
        <Input 
          id="name" 
          type="text" 
          bind:value={name} 
          required 
          placeholder={t.placeholder_name} 
          class="rounded-lg border-zinc-200 dark:border-zinc-800 focus-visible:ring-zinc-950 dark:bg-zinc-950" 
        />
      </div>

      <div class="space-y-2">
        <Label for="slug" class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.field_slug}</Label>
        <Input 
          id="slug" 
          type="text" 
          bind:value={slug} 
          required 
          placeholder={t.placeholder_slug} 
          class="rounded-lg border-zinc-200 dark:border-zinc-800 focus-visible:ring-zinc-950 dark:bg-zinc-950" 
        />
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div class="space-y-2">
        <Label class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.field_parent}</Label>
        <Select.Root 
          type="single" 
          value={String(parent_id)} 
          onValueChange={(val) => parent_id = val}
        >
          <Select.Trigger class="w-full rounded-lg border-zinc-200 dark:border-zinc-800 focus:ring-zinc-950 bg-white dark:bg-zinc-950">
            {currentSelectTriggerText}
          </Select.Trigger>
          <Select.Content class="rounded-lg shadow-md border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
            {#each selectOptions as opt}
              <Select.Item value={opt.value} class="text-sm cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100">
                {opt.label}
              </Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      </div>

      <div class="space-y-2">
        <Label for="color" class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.field_color}</Label>
        <div class="flex items-center gap-3">
          <div class="relative w-full flex items-center">
            <Input 
              id="color-text" 
              type="text" 
              bind:value={color} 
              class="rounded-lg pl-12 border-zinc-200 dark:border-zinc-800 uppercase font-mono text-xs focus-visible:ring-zinc-950 dark:bg-zinc-950" 
            />
            <div class="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md border dark:border-zinc-700 shadow-sm overflow-hidden flex items-center justify-center">
              <input 
                id="color" 
                type="color" 
                bind:value={color} 
                class="absolute scale-150 cursor-pointer w-full h-full p-0 border-none bg-transparent" 
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-2">
      <Button 
        type="submit" 
        disabled={isSubmitting} 
        class="w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:opacity-90 rounded-lg text-sm font-medium h-10 shadow transition-all flex items-center justify-center gap-2"
      >
        {#if isSubmitting}
          <Loader2 class="size-4 animate-spin" />
          {t.toast_saving}
        {:else}
          <Save class="size-4" />
          {t.toast_save_action}
        {/if}
      </Button>
    </div>
  </form>
</div>